// ────────────────────────────────────────────────────────────────────────────
//  i18n — Sprachauswahl + useT() Hook
//
//  Verwendung:
//    const t = useT()
//    t('auth.login')               // → "Anmelden" / "Sign in"
//    t('profile.codeSentTo', { email: 'a@b.de' })  // {email}-Substitution
//
//  Erweiterung um neue Sprache:
//    1. neue Datei z.B. fr.ts (typed als Translation aus de.ts)
//    2. Hier in `LANGS` eintragen
//    3. ProfileLanguageCard option hinzufügen
//    Fertig — TypeScript zwingt zur vollständigen Übersetzung.
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react'
import { de, type Translation } from './de'
import { en } from './en'
import { getLanguage, setLanguage } from '../lib/api'

export type LangCode = 'de' | 'en'

export const LANGS: Record<LangCode, { label: string; flag: string; t: Translation }> = {
  de: { label: 'Deutsch', flag: '🇩🇪', t: de },
  en: { label: 'English', flag: '🇬🇧', t: en },
}

const listeners = new Set<(l: LangCode) => void>()

/// Default Sprache: erst lokal gespeicherte Wahl, dann Browser-Locale, dann DE
function detectInitialLang(): LangCode {
  if (typeof window === 'undefined') return 'de'
  const stored = (localStorage.getItem('custosoft.lang') ?? '').toLowerCase()
  if (stored.startsWith('en')) return 'en'
  if (stored.startsWith('de')) return 'de'
  const nav = (navigator.language ?? '').toLowerCase()
  if (nav.startsWith('en')) return 'en'
  return 'de'
}

let currentLang: LangCode = detectInitialLang()

export function getLang(): LangCode { return currentLang }

export function setLang(l: LangCode) {
  currentLang = l
  setLanguage(l)
  listeners.forEach(fn => fn(l))
}

/// Substituiert {placeholder}-Platzhalter aus einem Vars-Object.
function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s
  return s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`))
}

/// Pfad-basierter Lookup mit Punkt-Notation (z.B. "auth.login").
function pick(t: Translation, path: string): string {
  const parts = path.split('.')
  let cur: any = t
  for (const p of parts) {
    if (cur == null) return path
    cur = cur[p]
  }
  return typeof cur === 'string' ? cur : path
}

export type TFunction = (key: string, vars?: Record<string, string | number>) => string

export function useT(): TFunction {
  const [, setTick] = useState(0)
  useEffect(() => {
    const fn = () => setTick(x => x + 1)
    listeners.add(fn)
    return () => { listeners.delete(fn) }
  }, [])
  const t = LANGS[currentLang].t
  return (key, vars) => interpolate(pick(t, key), vars)
}

/// Aktive Sprache als Hook (für Toggle-UIs)
export function useLangCode(): LangCode {
  const [l, setL] = useState<LangCode>(currentLang)
  useEffect(() => {
    setL(currentLang)
    const fn = (n: LangCode) => setL(n)
    listeners.add(fn)
    return () => { listeners.delete(fn) }
  }, [])
  return l
}

/// Initialisierung: synchronisiert localStorage-Wert in die api-Lib
export function initI18n() {
  if (typeof window !== 'undefined') {
    setLanguage(currentLang)
  }
  // currentLang wurde bereits beim Modul-Init gesetzt — nichts mehr zu tun
  void getLanguage
}
